// astro vue will render empty page if import vue from 'vue'
// @ts-ignore
import { createApp } from 'vue/dist/vue.esm-bundler.js'
import { ref, computed, provide } from 'vue'
import { useStore } from '@nanostores/vue'
import hybridPage from '@/lib/hybridPage'
import {
	createGAEvent,
	GaGeneralEvent,
	GaGeneralPageName,
} from '@/assets/ts/customEvent'
import {
	setCurrentGaPageName,
	getVueAssetImagePath,
	useBreakpoints,
} from '@/lib/utils'
import BaseBreadcrumb from '@/components/ui/BaseBreadcrumb.vue'
import { getMcrRootUrl, getSiteBaseUrl } from '@/lib/siteBaseUrl'
import {
	fetchMercariBrandBlogList,
	fetchMercariBrandDetail,
} from '@/assets/ts/apiManagement'
import goErrorPage from '@/legacy/ts/goErrorPage'
import { type IMercariBrandDetail } from '@/types/MercariBrandDetail'
import Skeleton from '@/components/Skeleton.vue'
import BaseButton from '@/components/ui/button/BaseButton.vue'
import { isLogin } from '@/store/userStore'
import getSeoRedirectLink, { getPageUrl } from '@/lib/getSeoRedirect'
import TabbedFlowSteps from '@/components/TabbedFlowSteps.vue'
import MercariBrandCommunitySection from '@/pages/mercari-brand/_mercariBrandCommunitySection.vue'
import useSnackBar from '@/legacy/ts/useSnackBar'
import BsSnackBar from '@/legacy/components/SnackBar.vue'
import { type IBlogThumbnailData } from '@/types/Blog'
import BlogThumbnail from '@/components/BlogThumbnail.vue'
import ParagraghSkeleton from '@/components/ParagraghSkeleton.vue'
import { getMercariUrl, replaceBrand } from '@/lib/mercari'
import MercariBrandMerchantList from '@/pages/mercari-brand/_mercariBrandMerchantList.vue'
import MercariBrandPricing from '@/pages/mercari-brand/_mercariBrandPricing.vue'
import { checkExpressCheckoutEnable } from '@/legacy/ts/productSharing'
import { fetchPricingCurrencyBuyforyou } from '@/legacy/ts/buyforyou'

hybridPage()

createApp({
	name: 'Mercari Brand Page',
	components: {
		BaseBreadcrumb,
		Skeleton,
		BaseButton,
		TabbedFlowSteps,
		BsSnackBar,
		MercariBrandCommunitySection,
		BlogThumbnail,
		ParagraghSkeleton,
		MercariBrandMerchantList,
		MercariBrandPricing,
	},
	setup() {
		setCurrentGaPageName(GaGeneralPageName.MercariBrand)
		createGAEvent(GaGeneralEvent.PageLoad, {
			page_name: GaGeneralPageName.MercariBrand,
		})
		const MAX_BLOG_LIST_ITEMS = 4
		const { pageContent } = window
		const isLoginGlobal = useStore(isLogin)
		const { isTabletPortrait } = useBreakpoints()
		const isLoading = ref(true)
		const isBannerLoading = ref(true)
		const isBlogListLoading = ref(true)
		const brandDetail = ref<IMercariBrandDetail | null>(null)
		const blogList = ref<IBlogThumbnailData[]>(
			Array.from(
				{ length: MAX_BLOG_LIST_ITEMS },
				() => ({}) as IBlogThumbnailData,
			),
		)
		const isCommunityListEmpty = ref(false)
		const defaultBanner = {
			desktop: getVueAssetImagePath('mercari-banner-default-desktop.avif'),
			mobile: getVueAssetImagePath('mercari-banner-default-mobile.avif'),
		}
		const buttonList = computed(() => [
			{
				text: pageContent.landingArea.afterLogin.shippingProtection,
				link: `${getSiteBaseUrl()}/${getSeoRedirectLink(
					'protectionShipping',
				)}/`,
				gaButtonName: 'Shipping Protection',
			},
			{
				text: pageContent.landingArea.afterLogin.proxyProtection,
				link: `${getSiteBaseUrl()}/protection/shopping/`,
				gaButtonName: 'Shopping Protection',
			},
		])
		const { isShow, messages, dismiss, addMessage } = useSnackBar()

		const localCurrencyInfo = ref(null)
		const isExpressCheckoutEnable = ref(checkExpressCheckoutEnable())

		async function setLocalCurrencyInfo() {
			localCurrencyInfo.value = await fetchPricingCurrencyBuyforyou()
		}

		const flowIcons = {
			tab_1: [
				'warehouse',
				'tracking_number',
				'consolidate_new',
				'delivery_truck',
			],
			tab_2: ['place_order', 'payment', 'consolidate_new', 'delivery_truck'],
		}

		const idMercariBrand = computed(() => {
			if (import.meta.env.PROD) {
				// /shopping-guides/chiikawa/
				const path = window.location.pathname
				const id = path.split('/').filter(Boolean).pop()
				return id ?? ''
			}
			return new URLSearchParams(window.location.search).get('id') ?? ''
		})

		const currentPageUrl = computed(() => {
			return getPageUrl(GaGeneralPageName.MercariBrand, {
				id: idMercariBrand.value,
			})
		})

		const breadcrumbs = computed(() => [
			{
				displayName: window.pageContent.breadcrumbs.home,
				categoryId: 'home',
				link: getSiteBaseUrl(),
			},
			{
				displayName: window.pageContent.breadcrumbs.shoppingGuide,
				categoryId: 'shoppingGuide',
				link: `${getSiteBaseUrl()}/shopping-guides/`,
			},
			{
				displayName: brandDetail.value?.name ?? '',
				categoryId: 'brand',
				link: currentPageUrl.value,
			},
		])

		const currentBlogSectionTitle = computed(() => {
			return replaceBrand(pageContent.blog.title, brandDetail.value?.name ?? '')
		})

		const currentCalloutContent = computed(() => {
			if (!brandDetail.value) return ''
			return brandDetail.value.callout_content
		})

		function transformPostsToBlogList(
			posts: IMercariBrandDetail['posts'],
		): IBlogThumbnailData[] {
			return posts.map(post => ({
				title: post.title,
				link: post.link,
				img_medium: post.img_medium,
				img_thumbnail: post.img_medium,
				img_large: post.img_medium,
				date: '',
				excerpt: '',
				tags: [],
				categories: [],
			}))
		}

		async function init() {
			try {
				if (!idMercariBrand.value) {
					goErrorPage()
					return
				}

				const res = await fetchMercariBrandDetail(idMercariBrand.value)
				window.BsHeaderVm.setTypeSuccessDialog('mercariBrand')
				if (res) {
					brandDetail.value = res
					// Initialize blogList with posts from brandDetail
					if (res.posts && res.posts.length >= MAX_BLOG_LIST_ITEMS) {
						blogList.value = transformPostsToBlogList(res.posts).slice(
							0,
							MAX_BLOG_LIST_ITEMS,
						)
						isBlogListLoading.value = false
					} else {
						fetchBlogList()
					}
				}
				if (isExpressCheckoutEnable.value) {
					setLocalCurrencyInfo()
				}
			} catch (error) {
				console.error(error)
				goErrorPage()
			} finally {
				isLoading.value = false
			}
		}

		async function fetchBlogList() {
			if (!brandDetail.value) return
			try {
				const res = await fetchMercariBrandBlogList(
					brandDetail.value.name,
					brandDetail.value.fallback_blog_category,
				)
				if (res) {
					let existingPosts: IBlogThumbnailData[] = []
					if (brandDetail.value.posts && brandDetail.value.posts.length > 0) {
						existingPosts = transformPostsToBlogList(brandDetail.value.posts)
					}
					blogList.value = [...existingPosts, ...res].slice(
						0,
						MAX_BLOG_LIST_ITEMS,
					)
				}
			} catch (error) {
				console.error(error)
				throw error
			} finally {
				isBlogListLoading.value = false
			}
		}

		function scrollToCommunity() {
			document
				.getElementById('community')
				?.scrollIntoView({ behavior: 'smooth' })
		}

		const currentBanner = computed(() => {
			if (!brandDetail.value) return ''
			if (isTabletPortrait.value) {
				return brandDetail.value.image.desktop || defaultBanner.desktop
			}
			return brandDetail.value.image.mobile || defaultBanner.mobile
		})

		function loadBannerDone() {
			isBannerLoading.value = false
		}

		function handleBannerButtonClick() {
			sendGeneralButtonClickEvent('Sign up - Hero')
			showAuthPanel()
		}

		function showAuthPanel() {
			window.BsHeaderVm.showAuthPanel()
		}

		const formattedTabFlowStepsContent = computed(() => {
			return replaceBrand(
				pageContent.flowContent,
				brandDetail.value?.name ?? '',
			)
		})

		const BUTTON_NAME = {
			startShipping: 'Start Shipping',
			startShopping: 'Start Shopping',
			getAddress: 'Get Address',
			exploreGlobal: 'Explore Global',
		}
		const BUTTON_CONFIGS = {
			loggedIn: {
				0: {
					action: () => {
						window.location.href = `${getMcrRootUrl()}/warehouse/`
					},
					buttonName: BUTTON_NAME.startShipping,
				},
				1: {
					action: () => {
						if (!isCommunityListEmpty.value) {
							scrollToCommunity()
						} else {
							window.location.href = getMercariUrl('search', {
								searchQuery: brandDetail.value?.name,
							})
						}
					},
					buttonName: BUTTON_NAME.startShopping,
				},
			},
			loggedOut: {
				0: {
					action: () => {
						showAuthPanel()
					},
					buttonName: BUTTON_NAME.getAddress,
				},
				1: {
					action: () => {
						showAuthPanel()
					},
					buttonName: BUTTON_NAME.exploreGlobal,
				},
			},
		}

		function handleButtonClick(index: number) {
			const config = isLoginGlobal.value
				? BUTTON_CONFIGS.loggedIn
				: BUTTON_CONFIGS.loggedOut
			const buttonConfig = config[index as keyof typeof config]

			if (!buttonConfig) {
				console.warn(`No configuration found for button index: ${index}`)
				return
			}

			sendGeneralButtonClickEvent(buttonConfig.buttonName)
			buttonConfig.action()
		}

		function sendGeneralButtonClickEvent(buttonName: string) {
			createGAEvent(GaGeneralEvent.ButtonClick, {
				page_name: GaGeneralPageName.MercariBrand,
				button_name: buttonName,
			})
		}

		function onClickFooterButton() {
			sendGeneralButtonClickEvent('Sign up - Bottom')
			showAuthPanel()
		}

		function notifyListEmpty() {
			isCommunityListEmpty.value = true
		}

		init()

		provide('addMessage', addMessage)

		return {
			isLoginGlobal,
			pageContent,
			isLoading,
			isTabletPortrait,
			isBannerLoading,
			breadcrumbs,
			brandDetail,
			currentBanner,
			buttonList,
			flowIcons,
			isShow,
			messages,
			idMercariBrand,
			formattedTabFlowStepsContent,
			currentCalloutContent,
			isBlogListLoading,
			blogList,
			currentBlogSectionTitle,
			isCommunityListEmpty,
			localCurrencyInfo,
			dismiss,
			loadBannerDone,
			handleBannerButtonClick,
			handleButtonClick,
			onClickFooterButton,
			sendGeneralButtonClickEvent,
			notifyListEmpty,
		}
	},
}).mount('#bsMain')
